/**
 * onboarding-entry.tsx — các lối vào / lối về trang "Bắt đầu" (/onboarding) nằm trong khung ứng dụng:
 *   - `useOnboardingEntry`: trạng thái dùng chung (chỉ gọi API cho chủ sở hữu không xem hộ).
 *   - `OnboardingResumeBar`: nút nổi "Tiếp tục thiết lập (x/2)" có vòng tiến độ, góc dưới phải mọi trang.
 *   - `OnboardingBackBar`: dải "← Quay lại trang Bắt đầu" ở đầu trang đích khi URL có `from=onboarding`.
 * Mục "Bắt đầu" trên menu trái nằm ở side-nav.tsx (dùng `useOnboardingEntry`).
 */
import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Link, useLocation } from 'react-router-dom'
import { ArrowLeft, ChevronRight, X } from 'lucide-react'
import { useAuthStore } from '@/stores/auth-store'
import { useOnboardingLiveRefresh, useOnboardingStatus } from '@/hooks/use-onboarding'
import { useOnboardingReturn } from '@/hooks/use-onboarding-return'
import { getSocket } from '@/lib/socket'
import { cn } from '@/lib/utils'
import {
  ONBOARDING_PATH,
  clearLegacyResumeClosed,
  closeResumeForSession,
  computeSteps,
  isResumeClosed,
  mayNeedOnboarding,
  onboardingReturnPath,
  readOnboardingOrigin,
  resetResumeClosed,
  resumeBarVisible,
  showOnboardingEntry,
} from '@/lib/onboarding'
import { ProgressRing } from './progress-ring'

// "×" chỉ ẩn thanh nổi trong lượt đăng nhập hiện tại: dọn khoá cũ (bản trước nhớ qua tải lại trang)
// và quên lựa chọn khi đăng xuất.
clearLegacyResumeClosed()
useAuthStore.subscribe((s, prev) => {
  if (prev.user && !s.user) resetResumeClosed()
})

export function useOnboardingEntry() {
  const user = useAuthStore((s) => s.user)
  const status = useOnboardingStatus(mayNeedOnboarding(user))
  const steps = computeSteps(status.data)
  return { user, status: status.data, steps, show: showOnboardingEntry(status.data, user) }
}

export function OnboardingResumeBar() {
  const { t } = useTranslation('sharedMisc')
  const { pathname } = useLocation()
  const { user, status, steps, show } = useOnboardingEntry()
  const [closed, setClosed] = useState(() => isResumeClosed(user?.id))
  useEffect(() => setClosed(isResumeClosed(user?.id)), [user?.id])
  // Chỉ nghe socket khi còn việc để làm (cả trên chính trang /onboarding).
  useOnboardingLiveRefresh(show)

  if (!resumeBarVisible({ pathname, status, user, closedThisSession: closed })) return null
  // Hộp thư có ô soạn tin ở đáy phải — đẩy nút lên để không che nút Gửi.
  const onInbox = pathname.startsWith('/conversations')

  return (
    // Lời nhắc nổi bật (style ở index.css, khối .onb-resume): viền gradient chảy màu, quầng sáng "thở",
    // vệt sáng lướt qua thân nút, vòng tiến độ xanh có nhịp toả sóng. Giảm chuyển động → đứng yên.
    // Điện thoại: ẩn hẳn (che nội dung) — lối vào "Bắt đầu" nằm trong hộp "Thêm", tab "Thêm" có chấm đỏ nhắc việc.
    <div className={cn('onb-resume fixed right-5 z-40 max-md:hidden', onInbox ? 'bottom-24' : 'bottom-5')}>
      <div className="onb-resume__inner flex items-center">
        <Link
          to={ONBOARDING_PATH}
          className="flex items-center gap-2.5 rounded-full py-1.5 pl-1.5 pr-2 text-sm font-semibold"
        >
          <span className="relative inline-flex">
            <span aria-hidden className="onb-resume__ping absolute inset-0 rounded-full bg-primary/40" />
            <ProgressRing value={steps.doneCount} total={steps.total} size={30} solid className="relative" />
          </span>
          <span className="flex flex-col leading-tight">
            <span>{t('onboardingEntry.resumeTitle')}</span>
            <span className="text-[11px] font-normal text-muted-foreground">
              {t('onboardingEntry.resumeStepsLeft', { count: steps.total - steps.doneCount })}
            </span>
          </span>
          <ChevronRight className="onb-resume__chev h-4 w-4 text-primary" />
        </Link>
        <button
          type="button"
          onClick={() => {
            closeResumeForSession(user?.id)
            setClosed(true)
          }}
          className="relative z-10 mr-1.5 rounded-full p-1 text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
          aria-label={t('onboardingEntry.hideThisSession')}
          title={t('onboardingEntry.hideThisSession')}
        >
          <X className="h-3.5 w-3.5" />
        </button>
      </div>
    </div>
  )
}

/**
 * Dải nhỏ đầu trang đích khi được mở từ trang Bắt đầu (`?from=onboarding`). Với bước 2 (trả lời tin đầu
 * trong Hộp thư), tin hệ thống đầu tiên (`onboarding:progress` reason `first-reply`) cũng tính là xong bước.
 */
export function OnboardingBackBar() {
  const { t } = useTranslation('sharedMisc')
  const { pathname, search } = useLocation()
  const origin = readOnboardingOrigin(new URLSearchParams(search))
  const { step, complete } = useOnboardingReturn()
  const onOnboarding = pathname === ONBOARDING_PATH
  const fromOnboarding = !!origin

  useEffect(() => {
    if (!fromOnboarding || onOnboarding || step !== 2) return
    const socket = getSocket()
    const onProgress = (p: { reason?: string } | undefined) => {
      if (p?.reason === 'first-reply') complete(2)
    }
    socket.on('onboarding:progress', onProgress)
    return () => {
      socket.off('onboarding:progress', onProgress)
    }
  }, [fromOnboarding, onOnboarding, step, complete])

  if (!origin || onOnboarding) return null
  return (
    <div className="flex shrink-0 items-center gap-2 border-b bg-primary/[0.06] px-4 py-1.5 text-sm">
      <Link
        to={onboardingReturnPath(origin.step)}
        className="flex shrink-0 items-center gap-1.5 whitespace-nowrap font-medium text-primary hover:underline"
      >
        <ArrowLeft className="h-4 w-4" /> {t('onboardingEntry.backToOnboarding')}
      </Link>
      {origin.step && (
        <span className="hidden min-w-0 truncate text-xs text-muted-foreground sm:inline">
          {t('onboardingEntry.backHint', { step: origin.step })}
        </span>
      )}
    </div>
  )
}
